<template>
  <div class="ep-page">

    <!-- ── Tabs: Regular / Overtime ───────────────────────── -->
    <div class="flex items-center gap-1 p-1 rounded-xl self-start w-fit"
         style="background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.07)">
      <button @click="switchTab('regular')"
              :class="['ep-btn ep-btn-sm', activeTab === 'regular' ? 'ep-btn-primary' : 'ep-btn-ghost']">
        <Clock class="w-4 h-4" /> {{ t('empHistory.regularAttendance') }}
      </button>
      <button @click="switchTab('overtime')"
              :class="['ep-btn ep-btn-sm', activeTab === 'overtime' ? 'ep-btn-primary' : 'ep-btn-ghost']">
        <Zap class="w-4 h-4" /> {{ t('empHistory.overtime') }}
      </button>
    </div>

    <!-- ── Quick Stats ────────────────────────────────────── -->
    <div class="grid grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">
      <div class="ep-card ep-card-hover p-4 sm:p-5 flex items-center gap-3.5">
        <div class="ep-stat-icon ep-stat-indigo"><Clock class="w-5 h-5" /></div>
        <div class="min-w-0">
          <p class="text-xs font-medium text-slate-500">{{ t('empHistory.thisWeek') }}</p>
          <p class="text-xl font-bold text-slate-100 tabular-nums truncate">{{ formatHours(summaryData.weekly_hours) }}</p>
        </div>
      </div>
      <div class="ep-card ep-card-hover p-4 sm:p-5 flex items-center gap-3.5">
        <div class="ep-stat-icon ep-stat-emerald"><Calendar class="w-5 h-5" /></div>
        <div class="min-w-0">
          <p class="text-xs font-medium text-slate-500">{{ t('empHistory.thisMonth') }}</p>
          <p class="text-xl font-bold text-slate-100 tabular-nums truncate">{{ formatHours(summaryData.monthly_hours) }}</p>
        </div>
      </div>
      <div class="ep-card ep-card-hover p-4 sm:p-5 flex items-center gap-3.5">
        <div class="ep-stat-icon ep-stat-amber"><TrendingUp class="w-5 h-5" /></div>
        <div class="min-w-0">
          <p class="text-xs font-medium text-slate-500">{{ t('empHistory.totalSessions') }}</p>
          <p class="text-xl font-bold text-slate-100 tabular-nums truncate">{{ summaryData.total_sessions }}</p>
        </div>
      </div>
      <div class="ep-card ep-card-hover p-4 sm:p-5 flex items-center gap-3.5">
        <div class="ep-stat-icon ep-stat-violet"><Target class="w-5 h-5" /></div>
        <div class="min-w-0">
          <p class="text-xs font-medium text-slate-500">{{ t('empHistory.avgDaily') }}</p>
          <p class="text-xl font-bold text-slate-100 tabular-nums truncate">{{ formatHours(summaryData.average_hours_per_day) }}</p>
        </div>
      </div>
    </div>

    <!-- ── Filters ────────────────────────────────────────── -->
    <div class="ep-card p-4 sm:p-5">
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        <div>
          <label class="ep-label">{{ t('empHistory.dateRange') }}</label>
          <select v-model="filters.dateRange" @change="applyFilters" class="ep-select">
            <option value="week">{{ t('empHistory.thisWeek') }}</option>
            <option value="month">{{ t('empHistory.thisMonth') }}</option>
            <option value="quarter">{{ t('empHistory.thisQuarter') }}</option>
            <option value="custom">{{ t('empHistory.customRange') }}</option>
          </select>
        </div>
        <div>
          <label class="ep-label">{{ t('common.status') }}</label>
          <select v-model="filters.status" @change="applyFilters" class="ep-select">
            <option value="">{{ t('empHistory.allRecords') }}</option>
            <option value="completed">{{ t('empHistory.completed') }}</option>
            <option value="active">{{ t('empHistory.inProgress') }}</option>
          </select>
        </div>
        <div class="flex items-end">
          <button @click="resetFilters" class="ep-btn ep-btn-secondary w-full">{{ t('empHistory.resetFilters') }}</button>
        </div>
      </div>

      <!-- Custom Date Range -->
      <div v-if="filters.dateRange === 'custom'" class="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        <div>
          <label class="ep-label">{{ t('empHistory.startDate') }}</label>
          <input v-model="filters.startDate" type="date" @change="applyFilters" class="ep-input" />
        </div>
        <div>
          <label class="ep-label">{{ t('empHistory.endDate') }}</label>
          <input v-model="filters.endDate" type="date" @change="applyFilters" class="ep-input" />
        </div>
      </div>
    </div>

    <!-- ── Work Sessions ──────────────────────────────────── -->
    <div class="ep-card overflow-hidden">
      <div class="flex items-center justify-between gap-3 px-5 py-4" style="border-bottom: 1px solid rgba(255,255,255,0.06)">
        <div>
          <h3 class="ep-section-title">
            <HistoryIcon class="w-4 h-4 text-indigo-400" />
            {{ activeTab === 'overtime' ? t('empHistory.overtimeSessions') : t('empHistory.workSessions') }}
          </h3>
          <p class="text-xs text-slate-500 mt-1">
            {{ t('empHistory.showing') }} {{ paginatedSessions?.length || 0 }} {{ t('empHistory.of') }} {{ totalSessions }} {{ t('empHistory.sessions') }}
          </p>
        </div>
        <button @click="exportHistory" class="ep-btn ep-btn-secondary ep-btn-sm shrink-0">
          <Download class="w-4 h-4" /> {{ t('empHistory.export') }}
        </button>
      </div>

      <!-- Desktop table -->
      <div class="hidden md:block overflow-x-auto">
        <table class="ep-table">
          <thead>
            <tr>
              <th>{{ t('empHistory.date') }}</th>
              <th>{{ t('empHistory.clockIn') }}</th>
              <th>{{ t('empHistory.clockOut') }}</th>
              <th>{{ t('empHistory.hours') }}</th>
              <th>{{ t('empHistory.status') }}</th>
              <th class="text-right">{{ t('empHistory.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="session in (paginatedSessions || [])" :key="session.id">
              <td class="font-medium text-slate-200">{{ formatDate(session.date) }}</td>
              <td class="tabular-nums">{{ session.clock_in ? formatTime(session.clock_in) : '—' }}</td>
              <td class="tabular-nums">{{ session.clock_out ? formatTime(session.clock_out) : '—' }}</td>
              <td>
                <span :class="['font-semibold tabular-nums', session.status === 'complete' ? 'text-emerald-400' : 'text-amber-400']">
                  {{ formatHours(session.hours_worked) }}
                </span>
              </td>
              <td>
                <div class="flex items-center gap-1.5">
                  <span :class="getSessionStatusClass(session)">
                    <span class="ep-dot"></span>{{ getSessionStatusText(session) }}
                  </span>
                  <span v-if="session.is_overtime" :class="getOvertimeBadgeClass(session)">{{ getOvertimeBadgeText(session) }}</span>
                </div>
              </td>
              <td class="text-right">
                <button @click="viewSessionDetails(session)"
                        class="text-xs font-medium text-indigo-400 hover:text-indigo-300 transition-colors">
                  {{ t('empHistory.viewDetails') }}
                </button>
              </td>
            </tr>

            <tr v-if="(paginatedSessions || []).length === 0">
              <td colspan="6">
                <div class="ep-empty">
                  <Clock class="w-10 h-10" />
                  <p class="text-sm font-medium text-slate-400">{{ t('empHistory.noSessionsFound') }}</p>
                  <p class="text-xs text-slate-600 mt-1">{{ t('empHistory.adjustFilters') }}</p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile cards -->
      <div class="md:hidden p-3 space-y-2.5">
        <div v-if="(paginatedSessions || []).length === 0" class="ep-empty">
          <Clock class="w-10 h-10" />
          <p class="text-sm font-medium text-slate-400">{{ t('empHistory.noSessionsFound') }}</p>
          <p class="text-xs text-slate-600 mt-1">{{ t('empHistory.adjustFilters') }}</p>
        </div>

        <button v-for="session in (paginatedSessions || [])" :key="session.id"
                @click="viewSessionDetails(session)"
                class="w-full text-left px-4 py-3.5 rounded-xl transition-colors hover:bg-white/[0.04] active:bg-white/[0.06]"
                style="background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.05)">
          <div class="flex items-center justify-between mb-2">
            <p class="text-sm font-semibold text-slate-200">{{ formatDate(session.date) }}</p>
            <div class="flex items-center gap-1.5">
              <span :class="getSessionStatusClass(session)">{{ getSessionStatusText(session) }}</span>
              <span v-if="session.is_overtime" :class="getOvertimeBadgeClass(session)">{{ getOvertimeBadgeText(session) }}</span>
            </div>
          </div>
          <div class="flex items-center justify-between text-xs text-slate-500">
            <span class="tabular-nums">
              {{ session.clock_in ? formatTime(session.clock_in) : '—' }}
              →
              {{ session.clock_out ? formatTime(session.clock_out) : '—' }}
            </span>
            <span :class="['font-semibold tabular-nums text-sm', session.status === 'complete' ? 'text-emerald-400' : 'text-amber-400']">
              {{ formatHours(session.hours_worked) }}
            </span>
          </div>
        </button>
      </div>

      <!-- Pagination -->
      <div class="px-4 sm:px-5 py-3.5 flex items-center justify-between gap-3"
           style="border-top: 1px solid rgba(255,255,255,0.06)">
        <p class="hidden sm:block text-xs text-slate-500">
          Showing {{ (currentPage - 1) * recordsPerPage + 1 }}–{{ Math.min(currentPage * recordsPerPage, totalSessions) }} of {{ totalSessions }}
        </p>
        <div class="flex items-center gap-1 mx-auto sm:mx-0">
          <button @click="previousPage" :disabled="currentPage === 1"
                  class="ep-btn ep-btn-secondary ep-btn-sm !px-2">
            <ChevronLeft class="w-4 h-4" />
          </button>
          <button v-for="page in visiblePages" :key="page"
                  @click="goToPage(page)"
                  :disabled="page === '...'"
                  :class="['ep-btn ep-btn-sm min-w-[2.1rem]',
                           page === currentPage ? 'ep-btn-primary' : 'ep-btn-ghost']">
            {{ page }}
          </button>
          <button @click="nextPage" :disabled="currentPage === totalPages"
                  class="ep-btn ep-btn-secondary ep-btn-sm !px-2">
            <ChevronRight class="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>

    <!-- ── Session Detail Modal ───────────────────────────── -->
    <div v-if="showDetailModal" class="ep-modal-backdrop" @click.self="closeDetailModal">
      <div class="ep-modal max-w-lg">
        <div class="flex items-center justify-between px-6 py-4"
             style="border-bottom: 1px solid rgba(255,255,255,0.07)">
          <h3 class="text-base font-semibold text-slate-100">{{ t('empHistory.sessionDetails') }}</h3>
          <button @click="closeDetailModal"
                  class="p-1.5 rounded-lg text-slate-500 hover:text-slate-300 hover:bg-white/5 transition-colors">
            <X class="w-5 h-5" />
          </button>
        </div>

        <div v-if="selectedRecord" class="p-6 space-y-5">
          <!-- Basic info -->
          <div class="grid grid-cols-2 gap-4">
            <div>
              <p class="ep-label">{{ t('empHistory.date') }}</p>
              <p class="text-sm font-medium text-slate-200">{{ formatDate(selectedRecord.date) }}</p>
            </div>
            <div>
              <p class="ep-label">{{ t('empHistory.status') }}</p>
              <div class="flex items-center gap-1.5">
                <span :class="getSessionStatusClass(selectedRecord)">{{ getSessionStatusText(selectedRecord) }}</span>
                <span v-if="selectedRecord.is_overtime" :class="getOvertimeBadgeClass(selectedRecord)">{{ getOvertimeBadgeText(selectedRecord) }}</span>
              </div>
            </div>
          </div>

          <!-- Times -->
          <div class="rounded-xl p-4"
               style="background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.06)">
            <h4 class="text-sm font-semibold text-slate-200 mb-3">{{ t('empHistory.timeDetails') }}</h4>
            <div class="grid grid-cols-2 gap-4">
              <div>
                <p class="ep-label">{{ t('empHistory.clockIn') }}</p>
                <p class="text-sm text-slate-300 tabular-nums">
                  {{ selectedRecord.clock_in ? formatDateTime(selectedRecord.clock_in) : t('empHistory.notRecorded') }}
                </p>
              </div>
              <div>
                <p class="ep-label">{{ t('empHistory.clockOut') }}</p>
                <p class="text-sm text-slate-300 tabular-nums">
                  {{ selectedRecord.clock_out ? formatDateTime(selectedRecord.clock_out) : t('empHistory.notRecorded') }}
                </p>
                <span v-if="selectedRecord.clock_out_by_system" class="ep-badge ep-badge-info mt-1.5">
                  <Bot class="w-3 h-3" /> {{ t('empHistory.clockedOutBySystem') }}
                </span>
              </div>
            </div>
            <div class="mt-4 pt-4" style="border-top: 1px solid rgba(255,255,255,0.06)">
              <p class="ep-label">Hours Worked</p>
              <p class="text-2xl font-bold text-emerald-400 tabular-nums">{{ formatHours(selectedRecord.hours_worked) }}</p>
            </div>
          </div>

          <!-- Time capped notice -->
          <div v-if="selectedRecord.time_capped"
               class="rounded-xl p-4 flex items-start gap-3"
               style="background: rgba(251,191,36,0.07); border: 1px solid rgba(251,191,36,0.25)">
            <Scissors class="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
            <div class="text-xs text-amber-200/90 leading-relaxed">
              <p class="font-semibold text-amber-200">{{ t('empHistory.timeCapped') }}</p>
              <p class="mt-0.5">{{ getCappingReasonText(selectedRecord.capping_reason) }}</p>
              <p v-if="selectedRecord.payroll_clock_in" class="mt-0.5">
                {{ t('empHistory.payrollFrom') }}
                <span class="font-semibold tabular-nums">{{ formatDateTime(selectedRecord.payroll_clock_in) }}</span>.
              </p>
              <p v-if="selectedRecord.payroll_clock_out" class="mt-0.5">
                {{ t('empHistory.payrollUntil') }}
                <span class="font-semibold tabular-nums">{{ formatDateTime(selectedRecord.payroll_clock_out) }}</span>.
              </p>
            </div>
          </div>

          <!-- Incomplete notice -->
          <div v-if="selectedRecord.incomplete"
               class="rounded-xl p-4 flex items-start gap-3"
               style="background: rgba(251,191,36,0.07); border: 1px solid rgba(251,191,36,0.25)">
            <TrendingUp class="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
            <p class="text-xs text-amber-200/90 leading-relaxed">
              <template v-if="!selectedRecord.clock_in && selectedRecord.clock_out">
                {{ t('empHistory.noClockIn') }}
              </template>
              <template v-else-if="selectedRecord.clock_in && !selectedRecord.clock_out">
                {{ t('empHistory.stillInProgress') }}
              </template>
              <template v-else>
                {{ t('empHistory.incompleteRecords') }}
              </template>
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
const { t } = useI18n();
import {
  Clock,
  Calendar,
  Download,
  TrendingUp,
  Target,
  ChevronLeft,
  ChevronRight,
  X,
  Zap,
  Bot,
  Scissors,
  History as HistoryIcon,
} from 'lucide-vue-next';
import axios from 'axios';
import { useAuthStore } from '@/stores/auth';

// Reactive data
const authStore = useAuthStore();
const workSessions = ref([]);
const dailyBreakdown = ref([]);
const summaryData = ref({
  total_hours: 0,
  weekly_hours: 0,
  monthly_hours: 0,
  total_sessions: 0,
  complete_sessions: 0,
  incomplete_sessions: 0,
  average_hours_per_day: 0
});
const paginationInfo = ref({
  current_page: 1,
  per_page: 10,
  total: 0,
  total_pages: 0,
  has_next: false,
  has_prev: false
});

const filters = reactive({
  dateRange: 'month',
  status: '',
  startDate: '',
  endDate: '',
});

// Resolve the selected date range preset into concrete start/end dates
const toYMD = date => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const resolveDateRange = () => {
  if (filters.dateRange === 'custom') {
    return { startDate: filters.startDate, endDate: filters.endDate };
  }
  const now = new Date();
  let start;
  if (filters.dateRange === 'week') {
    start = new Date(now);
    start.setDate(now.getDate() - now.getDay()); // start of current week (Sunday)
  } else if (filters.dateRange === 'quarter') {
    start = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
  } else {
    start = new Date(now.getFullYear(), now.getMonth(), 1); // month
  }
  return { startDate: toYMD(start), endDate: toYMD(now) };
};

const activeTab = ref('regular');
const currentPage = ref(1);
const recordsPerPage = ref(10);

const switchTab = tab => {
  if (activeTab.value === tab) return;
  activeTab.value = tab;
  currentPage.value = 1;
  fetchAttendanceHistory();
};
const showDetailModal = ref(false);
const selectedRecord = ref(null);

// Computed properties
const filteredSessions = computed(() => {
  let filtered = workSessions.value || [];

  // Apply status filter
  if (filters.status) {
    filtered = filtered.filter(session => {
      if (filters.status === 'completed') return session.status === 'complete';
      if (filters.status === 'active') return session.status === 'incomplete';
      return true;
    });
  }

  return filtered;
});

const totalSessions = computed(() => paginationInfo.value?.total || workSessions.value?.length || 0);
const totalPages = computed(() => paginationInfo.value?.total_pages || Math.ceil((workSessions.value?.length || 0) / recordsPerPage.value));

const paginatedSessions = computed(() => {
  // Use backend pagination if available, otherwise client-side pagination
  if (paginationInfo.value?.total > 0) {
    return filteredSessions.value || []; // Backend already paginated; status filter applies per page
  }
  const start = (currentPage.value - 1) * recordsPerPage.value;
  const end = start + recordsPerPage.value;
  return (filteredSessions.value || []).slice(start, end);
});

const visiblePages = computed(() => {
  const pages = [];
  const total = totalPages.value;
  const current = currentPage.value;

  if (total <= 7) {
    for (let i = 1; i <= total; i++) {
      pages.push(i);
    }
  } else {
    if (current <= 4) {
      for (let i = 1; i <= 5; i++) pages.push(i);
      pages.push('...');
      pages.push(total);
    } else if (current >= total - 3) {
      pages.push(1);
      pages.push('...');
      for (let i = total - 4; i <= total; i++) pages.push(i);
    } else {
      pages.push(1);
      pages.push('...');
      for (let i = current - 1; i <= current + 1; i++) pages.push(i);
      pages.push('...');
      pages.push(total);
    }
  }

  return pages;
});

// Methods
const fetchAttendanceHistory = async () => {
  try {
    const params = new URLSearchParams({
      page: currentPage.value.toString(),
      limit: recordsPerPage.value.toString(),
      sessionType: activeTab.value,
    });

    // Add date filter parameters
    const { startDate, endDate } = resolveDateRange();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await axios.get(`/employee/attendance/history?${params}`);
    
    if (response.data.success && response.data.data) {
      const data = response.data.data;
      workSessions.value = data.sessions || [];
      dailyBreakdown.value = data.daily_breakdown || [];
      summaryData.value = data.summary || summaryData.value;
      paginationInfo.value = data.pagination || paginationInfo.value;
      
      console.log('📊 Fetched attendance history:', {
        sessions: workSessions.value.length,
        summary: summaryData.value,
        pagination: paginationInfo.value
      });
    }
  } catch (error) {
    console.error('Failed to fetch attendance history:', error);
    workSessions.value = [];
    dailyBreakdown.value = [];
  }
};

// Stats are now part of the main history API response, so no separate fetch needed

const applyFilters = () => {
  currentPage.value = 1;
  fetchAttendanceHistory();
};

const resetFilters = () => {
  Object.assign(filters, {
    dateRange: 'month',
    status: '',
    startDate: '',
    endDate: '',
  });
  currentPage.value = 1;
  fetchAttendanceHistory();
};

const formatDate = dateString => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatTime = timeString => {
  return new Date(timeString).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDateTime = dateTimeString => {
  return new Date(dateTimeString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatHours = hours => {
  if (!hours || hours === 0) return '0h';
  const wholeHours = Math.floor(hours);
  const minutes = Math.round((hours - wholeHours) * 60);
  return minutes > 0 ? `${wholeHours}h ${minutes}m` : `${wholeHours}h`;
};

const getSessionStatusClass = session => {
  if (session.status === 'complete')   return 'ep-badge ep-badge-success';
  if (session.status === 'incomplete') return 'ep-badge ep-badge-warning';
  if (session.status === 'orphaned')   return 'ep-badge ep-badge-danger';
  return 'ep-badge ep-badge-neutral';
};

const getSessionStatusText = session => {
  if (session.status === 'complete') return 'Complete';
  if (session.status === 'incomplete') return 'In Progress';
  if (session.status === 'orphaned') return 'Invalid';
  return 'Unknown';
};

const getCappingReasonText = reason => {
  const reasons = {
    after_work_hours: 'The clock-out happened after official working hours, so worked time was capped at the end of the work day.',
    before_work_hours: 'The clock-in happened before official working hours, so worked time was capped at the start of the work day.',
  };
  return reasons[reason] || (reason ? `Reason: ${reason.replace(/_/g, ' ')}` : 'Worked time was adjusted to the official schedule.');
};

// Overtime badge reflects the overtime session outcome, not just the flag
const getOvertimeBadgeClass = session => {
  if (session.overtime_status === 'rejected') return 'ep-badge ep-badge-danger';
  if (session.overtime_status === 'completed') return 'ep-badge ep-badge-success';
  return 'ep-badge ep-badge-warning';
};

const getOvertimeBadgeText = session => {
  if (session.overtime_status === 'rejected') return 'OT Rejected';
  if (session.overtime_status === 'completed') return 'OT Completed';
  if (session.overtime_status) return `OT ${session.overtime_status.charAt(0).toUpperCase()}${session.overtime_status.slice(1)}`;
  return 'OT';
};

const viewSessionDetails = session => {
  selectedRecord.value = session;
  showDetailModal.value = true;
};

const getModeClass = mode => {
  return mode === 'field' ? 'ep-badge ep-badge-info' : 'ep-badge ep-badge-neutral';
};

const getStatusClass = record => {
  if (record.status === 'pending')  return 'ep-badge ep-badge-warning';
  if (record.status === 'approved' || record.status === 'auto_approved') return 'ep-badge ep-badge-success';
  if (record.status === 'rejected') return 'ep-badge ep-badge-danger';
  return 'ep-badge ep-badge-neutral';
};

const getStatusText = record => {
  if (record.status === 'pending') return 'Pending Approval';
  if (record.status === 'approved' || record.status === 'auto_approved') return 'Approved';
  if (record.status === 'rejected') return 'Rejected';
  return record.status || 'Unknown';
};

const canEdit = record => {
  // Only allow editing if record is from today and is rejected
  const today = new Date().toDateString();
  const recordDate = new Date(record.timestamp || record.created_at).toDateString();
  return today === recordDate && record.status === 'rejected' && record.can_retry;
};

const viewDetails = record => {
  selectedRecord.value = record;
  showDetailModal.value = true;
};

const closeDetailModal = () => {
  showDetailModal.value = false;
  selectedRecord.value = null;
};

const editRecord = record => {
  // Implement edit functionality
  console.log('Edit record:', record);
};

const getPhotoUrl = photoPath => {
  return photoPath ? `/api/uploads/${photoPath}` : null;
};

const exportHistory = async () => {
  try {
    const params = new URLSearchParams({
      format: 'csv'
    });

    // Export the same date range currently shown
    const { startDate, endDate } = resolveDateRange();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await axios.get(`/employee/reports/attendance?${params}`, {
      responseType: 'blob',
    });

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
      'download',
      `attendance-history-${new Date().toISOString().split('T')[0]}.csv`
    );
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Failed to export history:', error);
    // Show user-friendly error message
    alert('Failed to export attendance history. Please try again.');
  }
};

const previousPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
    fetchAttendanceHistory();
  }
};

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
    fetchAttendanceHistory();
  }
};

const goToPage = page => {
  if (typeof page === 'number') {
    currentPage.value = page;
    fetchAttendanceHistory();
  }
};

// Lifecycle
onMounted(() => {
  fetchAttendanceHistory();
});
</script>
